import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import purchaseRequisitionService, {
  PurchaseRequisition,
  CreatePurchaseRequisitionPayload,
  PurchaseRequisitionFilters,
  EligibleSOLine,
} from '@/services/purchaseRequisition.service';
import type { RejectionPayload } from '@/services/rejection.service';

export function usePurchaseRequisitions(filters?: PurchaseRequisitionFilters) {
  return useQuery({
    queryKey: ['purchase-requisitions', filters],
    queryFn: () => purchaseRequisitionService.list(filters),
    staleTime: 30000,
  });
}

export function usePurchaseRequisition(id: string | undefined) {
  return useQuery({
    queryKey: ['purchase-requisitions', id],
    queryFn: () => purchaseRequisitionService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useEligibleSoLines(filters?: {
  customer_id?: string;
  from_date?: string;
  to_date?: string;
  order_status?: string;
  sales_order_ids?: string[];
}) {
  return useQuery({
    queryKey: ['purchase-requisitions', 'eligible-so-lines', filters],
    queryFn: () => purchaseRequisitionService.getEligibleSoLines(filters),
    enabled: true,
    staleTime: 10000,
  });
}

export function useCreatePurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreatePurchaseRequisitionPayload) => purchaseRequisitionService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      message.success('Purchase Request created successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create Purchase Request');
    },
  });
}

export function useUpdatePurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreatePurchaseRequisitionPayload> }) =>
      purchaseRequisitionService.update(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions', id] });
      message.success('Purchase Request updated successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update Purchase Request');
    },
  });
}

export function useSubmitPurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => purchaseRequisitionService.submit(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions', id] });
      message.success('Purchase Request submitted');
      const warning = (data as any)?.cost_control_warning;
      if (warning?.message) {
        message.warning(String(warning.message), 8);
      }
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to submit');
    },
  });
}

export function useApprovePurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => purchaseRequisitionService.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions', id] });
      message.success('Purchase Request approved');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to approve');
    },
  });
}

export function useRejectPurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectionPayload }) =>
      purchaseRequisitionService.reject(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions', id] });
      message.success('Purchase Request rejected');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to reject');
    },
  });
}

export function useCancelPurchaseRequisition() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => purchaseRequisitionService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requisitions', id] });
      message.success('Purchase Request cancelled');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to cancel');
    },
  });
}
